import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { legTypeHasChassis, noChassisReason } from '@api/Trinium/types';
import RenderCell from '@components/DataGrid/RenderCell';
import { defaultSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import { applyColumnQuickFilter } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useState } from 'react';
import TextField from '@forms/Components/TextInput';
import { resolveCopyCntrChassisToAllLegs } from '@api/Trinium/Endpoints/Leg/actions';
import { usePatchLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { EditableCell, resolveEditTargetRows } from '../types';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

/**
 * Allows searching "AAAA 0000-0" with values like "AAAA00000", "AAAA0000-0", etc.
 */
const normalizeChassisString = (str: string | undefined): string => str?.replace(/[\s-]/g, '').toLowerCase() ?? '';

const headerDisplayName = 'Chassis';

const chassisDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Chassis',
  minWidth: 50,
  width: 114,
  headerName: 'Chassis #',
  headerDisplayName,
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => (
    <RenderCell
      editable
      primarySlot={
        <span
          className={styles.bold}
          title={row.chassisId}>
          {row.chassisId}
        </span>
      }
      tertiarySlot={row.isChassisOwned === true && <span>Company Owned</span>}
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { chassisId } = row;
        const normalizedValue = normalizeChassisString(value);
        const normalizedChassis = normalizeChassisString(chassisId);
        return normalizedChassis.includes(normalizedValue);
      });
  },
  valueGetter: (_, row: DispatchLeg) => row.chassisId ?? '',
  sortable: true,
  sortComparator: defaultSortComparator,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <ChassisEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

/**
 * Component to render when the Chassis cell is clicked and enters edit mode
 */
const ChassisEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [patchLegs] = usePatchLegsMutation();
  const [chassisVal, setChassisVal] = useState(row.chassisId);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));
  const hasChassis = legTypeHasChassis(row.legType);

  const saveHandler = useCallback(async (scope: EditSaveScope) => {
    // Only need to call API if they changed the value
    if (chassisVal !== row.chassisId) {
      const cpyCntrChassisToAllLegs = await resolveCopyCntrChassisToAllLegs(row.isLive, chassisVal, row.chassisId, 'chassis number');
      const targetRows = resolveEditTargetRows(scope, row, selectedRows, (targetRow) => legTypeHasChassis(targetRow.legType));

      await patchLegs({
        terminalNumber,
        legIds: targetRows.map((targetRow) => targetRow.legId),
        leg: {
          chassisId: chassisVal,
          ...(cpyCntrChassisToAllLegs !== undefined && { cpyCntrChassisToAllLegs })
        }
      })
        .unwrap()
        .then((response) => {
          response.errors.forEach((error) => {
            appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
          });
        })
        .catch((error: unknown) => {
          appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
        });
    }
  }, [appDispatch, terminalNumber, patchLegs, chassisVal, row, selectedRows]);

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <TextField
      variant='standard'
      value={chassisVal}
      onChange={(event) => {
        setChassisVal(event.target.value);
      }}
      focused
      disabled={!hasChassis || isLoading}
      customProps={{
        labelText: 'Chassis',
        isDirty: false,
        errorText: undefined,
        onClear: () => {
          setChassisVal('');
        },
        disabledReason: !hasChassis ? noChassisReason : isLoading ? 'Updating…' : undefined
      }}
    />
  );
});

export default chassisDefinition;
